import React, { useState } from "react";
import { useSelector } from "react-redux";
import { isSuperAdmin } from '../../reducers/auth';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import SandsService from '../../services/sands.service';

const AdminSandS = () => {
  const isSuperAdminUser = useSelector(isSuperAdmin);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState('info');
  const [partNumber, setPartNumber] = useState('');
  const [connectionTest, setConnectionTest] = useState(null);

  // Show message helper
  const showMessage = (msg, type = 'info') => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(null), 10000); // Clear after 10 seconds
  };

  // Test API connection
  const handleTestConnection = async () => {
    setLoading(true);
    setConnectionTest(null);
    try {
      const result = await SandsService.testConnection();
      setConnectionTest(result.data);
      showMessage(result.message, 'success');
    } catch (error) {
      showMessage('Error testing connection: ' + (error.response?.data?.message || error.message), 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Load all styles
  const handleLoadAllStyles = async () => {
    if (!window.confirm('This will load all styles from S&S Activewear. This may take several minutes. Continue?')) {
      return;
    }

    setLoading(true);
    try {
      const result = await SandsService.loadAllStyles();
      const data = result.data;
      showMessage(
        `Successfully loaded S&S styles! Total: ${data.total}, Created: ${data.loaded}, Updated: ${data.updated}, Skipped: ${data.skipped}`,
        'success'
      );
    } catch (error) {
      showMessage('Error loading styles: ' + (error.response?.data?.message || error.message), 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Load products for all S&S styles in the database
  const handleLoadAllProducts = async () => {
    if (!window.confirm('This will load products for every S&S style already in the database. Styles must be loaded first. This may take a long time. Continue?')) {
      return;
    }

    setLoading(true);
    try {
      const result = await SandsService.loadAllProducts();
      const data = result.data;
      showMessage(
        `S&S products loaded! Styles: ${data.stylesProcessed}/${data.stylesTotal} processed, ${data.stylesFailed} failed, ${data.stylesSkipped} skipped. Products - Total: ${data.productsTotal}, Created: ${data.productsCreated}, Updated: ${data.productsUpdated}, Skipped: ${data.productsSkipped}`,
        data.stylesFailed > 0 ? 'warning' : 'success'
      );
    } catch (error) {
      showMessage('Error loading products: ' + (error.response?.data?.message || error.message), 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Load style and products by alphabroder #
  const handleLoadStyle = async (e) => {
    e.preventDefault();

    if (!partNumber.trim()) {
      showMessage('Please enter an alphabroder #', 'warning');
      return;
    }

    setLoading(true);
    try {
      const result = await SandsService.loadStyleByPartNumber(partNumber.trim());
      const styleMsg = `Style ${partNumber} ${result.data.result === 'created' ? 'created' : 'updated'} successfully!`;
      const p = result.products;
      let productsMsg = '';
      if (p && p.error) {
        productsMsg = ` Products failed: ${p.error}`;
      } else if (p) {
        productsMsg = ` Products - Total: ${p.total}, Created: ${p.created}, Updated: ${p.updated}, Skipped: ${p.skipped}`;
      }
      showMessage(styleMsg + productsMsg, p && p.error ? 'warning' : 'success');
      setPartNumber(''); // Clear input
    } catch (error) {
      showMessage('Error loading style: ' + (error.response?.data?.message || error.message), 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Check if user is super admin
  if (!isSuperAdminUser) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <Alert.Heading>Access Denied</Alert.Heading>
          <p>You must be a super administrator to access this page.</p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <h2 className="mb-4">S&S Activewear Integration</h2>

      {message && (
        <Alert variant={messageType} dismissible onClose={() => setMessage(null)}>
          {message}
        </Alert>
      )}

      <Row>
        {/* Test Connection Card */}
        <Col md={6} className="mb-4">
          <Card>
            <Card.Header as="h5" style={{ backgroundColor: '#f8f9fa' }}>
              Test API Connection
            </Card.Header>
            <Card.Body>
              <Card.Text>
                Test the connection to S&S Activewear API and view sample data.
              </Card.Text>
              <Button 
                variant="primary" 
                onClick={handleTestConnection}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Testing...
                  </>
                ) : (
                  'Test Connection'
                )}
              </Button>

              {connectionTest && (
                <div className="mt-3">
                  <Alert variant="success">
                    <strong>Connection Successful!</strong>
                    <div className="mt-2">
                      <strong>Total Styles Available:</strong> {connectionTest.stylesCount}
                    </div>
                    {connectionTest.sampleStyle && (
                      <div className="mt-2">
                        <strong>Sample Style:</strong>
                        <div style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                          <div><strong>Style Name:</strong> {connectionTest.sampleStyle.styleName}</div>
                          <div><strong>Brand:</strong> {connectionTest.sampleStyle.brandName}</div>
                          <div><strong>Title:</strong> {connectionTest.sampleStyle.title}</div>
                        </div>
                      </div>
                    )}
                  </Alert>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Load All Styles Card */}
        <Col md={6} className="mb-4">
          <Card>
            <Card.Header as="h5" style={{ backgroundColor: '#f8f9fa' }}>
              Load All Styles
            </Card.Header>
            <Card.Body>
              <Card.Text>
                Load style metadata for every style from S&S Activewear. This creates or updates styles only; use Load All Products afterward for colors and sizes.
              </Card.Text>
              <Alert variant="warning" className="mb-3">
                <small>
                  <strong>Warning:</strong> This operation may take several minutes to complete.
                </small>
              </Alert>
              <Button 
                variant="success" 
                onClick={handleLoadAllStyles}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Loading Styles...
                  </>
                ) : (
                  'Load All Styles'
                )}
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        {/* Load All Products Card */}
        <Col md={6} className="mb-4">
          <Card>
            <Card.Header as="h5" style={{ backgroundColor: '#f8f9fa' }}>
              Load All Products
            </Card.Header>
            <Card.Body>
              <Card.Text>
                Load products (colors, sizes, and prices) for every S&S style already in the database. Run Load All Styles first if styles are missing.
              </Card.Text>
              <Alert variant="warning" className="mb-3">
                <small>
                  <strong>Warning:</strong> This calls the S&S API once per style and may take a long time.
                </small>
              </Alert>
              <Button
                variant="success"
                onClick={handleLoadAllProducts}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Loading Products...
                  </>
                ) : (
                  'Load All Products'
                )}
              </Button>
            </Card.Body>
          </Card>
        </Col>

        {/* Load Style & Products Card */}
        <Col md={6} className="mb-4">
          <Card>
            <Card.Header as="h5" style={{ backgroundColor: '#f8f9fa' }}>
              Load Style & Products
            </Card.Header>
            <Card.Body>
              <Card.Text>
                Import a style and all of its products (colors, sizes, and prices) in one step using the alphabroder #.
              </Card.Text>
              <Form onSubmit={handleLoadStyle}>
                <Form.Group className="mb-3">
                  <Form.Label>Alphabroder #</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter alphabroder # (e.g., 00760)"
                    value={partNumber}
                    onChange={(e) => setPartNumber(e.target.value)}
                    disabled={loading}
                  />
                  <Form.Text className="text-muted">
                    Enter the alphabroder # (5-digit partNumber) from S&S Activewear.
                  </Form.Text>
                </Form.Group>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={loading || !partNumber.trim()}
                >
                  {loading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Loading...
                    </>
                  ) : (
                    'Load Style & Products'
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        {/* Information Card */}
        <Col md={6} className="mb-4">
          <Card>
            <Card.Header as="h5" style={{ backgroundColor: '#f8f9fa' }}>
              Information
            </Card.Header>
            <Card.Body>
              <Card.Title>About S&S Integration</Card.Title>
              <Card.Text>
                This integration allows you to import product styles from S&S Activewear directly into your catalog.
              </Card.Text>
              <ul style={{ fontSize: '0.875rem' }}>
                <li><strong>Source:</strong> All imported styles, colors, and sizes are marked with source "sands"</li>
                <li><strong>Single import:</strong> Load Style & Products brings in the style and all color/size combinations together</li>
                <li><strong>Load All Styles:</strong> Imports style metadata only</li>
                <li><strong>Load All Products:</strong> Loads products for every S&S style already in the database</li>
                <li><strong>Updates:</strong> Re-importing will update existing data. Refresh colors, prices, and inventory from the product details page</li>
                <li><strong>API:</strong> Uses S&S Activewear API v2</li>
                <li><strong>Authentication:</strong> Configured via environment variables</li>
              </ul>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AdminSandS;
